import {
    CommunityId,
    getDistanceLatLng,
    ILocation,
    isValidLocation,
    locationToCoordinates,
    makeLocation,
} from "@sp-crm/core";
import { ClientGraphqlError } from "backend/fetcher";
import {
    SearchResultsMapCluster,
    SearchResultsMapMarker,
} from "components/community-search/community-map/map-marker";
import {
    HoveredMapEntity,
    MapMarkerId,
} from "components/community-search/community-map/types";
import { Error } from "components/error";
import {
    GeoCenter,
    MapLoadingIndicator,
    useMapEvents,
    useMapScaleUnit,
} from "components/map/map-common";
import { mapLib } from "components/map/map-lib";
import { useDebouncedValue } from "components/shared/hooks";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { BoundsInput } from "generated/graphql";
import { useWindowSize } from "hooks/use-window-size";
import { produce } from "immer";
import { LngLat, LngLatBounds, PositionAnchor } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React from "react";
import Map, {
    FullscreenControl,
    GeolocateControl,
    LngLatBoundsLike,
    MapRef,
    NavigationControl,
    Popup,
    ScaleControl,
} from "react-map-gl/maplibre";
import { Link } from "react-router-dom";
import { NO_NAME } from "util/text";
import { MapErrorBoundary } from "./map-error-boundary";
import {
    MapSearchCluster,
    MapSearchCoordinate,
    MapSearchResults,
    MapView,
} from "./types";
import { BasemapPolicy, useMapStyle } from "./use-map-style";

const latLngToILocation = (latLng: LngLat | LngLat): ILocation =>
    makeLocation(latLng.lat, latLng.lng);

const defaultZoomLevel = 5;

const continentalUsCenter: ILocation = makeLocation(39.8283, -98.5795);
const continentalUsZoomLevel = 4;

export type InitialViewState =
    | { bounds: LngLatBoundsLike; fitBoundsOptions: { maxZoom: number } }
    | { longitude: number; latitude: number; zoom: number };

export const computeInitialViewState = (
    mapBounds: LngLatBounds | null,
    fallbackCenter: ILocation | null,
): InitialViewState => {
    if (mapBounds) {
        return {
            bounds: mapBounds as unknown as LngLatBoundsLike,
            fitBoundsOptions: { maxZoom: 10 },
        };
    }
    if (fallbackCenter) {
        return {
            longitude: fallbackCenter.loc[0],
            latitude: fallbackCenter.loc[1],
            zoom: defaultZoomLevel,
        };
    }
    return {
        longitude: continentalUsCenter.loc[0],
        latitude: continentalUsCenter.loc[1],
        zoom: continentalUsZoomLevel,
    };
};

interface ComponentState {
    isFreeMapView: boolean;
    showUpdateSearch: boolean;
    isLoading: boolean;
    bounds: BoundsInput | null;
    popupId: MapMarkerId | null;
    zoomLevel: number | null;
}

type Action =
    | { type: "endUserMapMove"; bounds: BoundsInput; zoomLevel: number }
    | { type: "endAutoMapMove" }
    | { type: "disableFreeMap" }
    | { type: "startExplicitBoundsSearch" }
    | { type: "startLoading" }
    | { type: "endLoading" }
    | { type: "clearPopup" }
    | { type: "setPopup"; popupId: MapMarkerId };

const reducer: React.Reducer<ComponentState, Action> = (state, action): ComponentState =>
    produce(state, draft => {
        if (action.type === "endUserMapMove") {
            draft.isFreeMapView = true;
            draft.bounds = action.bounds;
            draft.showUpdateSearch = true;
            draft.zoomLevel = action.zoomLevel;
        } else if (action.type === "endAutoMapMove") {
            draft.isFreeMapView = false;
        } else if (action.type === "disableFreeMap") {
            draft.isFreeMapView = false;
        } else if (action.type === "startExplicitBoundsSearch") {
            draft.showUpdateSearch = false;
        } else if (action.type === "startLoading") {
            draft.isLoading = true;
        } else if (action.type === "endLoading") {
            draft.isLoading = false;
        } else if (action.type === "clearPopup") {
            draft.popupId = null;
        } else if (action.type === "setPopup") {
            draft.popupId = action.popupId;
        }
    });

const initialState: ComponentState = {
    isFreeMapView: false,
    showUpdateSearch: false,
    isLoading: false,
    bounds: null,
    popupId: null,
    zoomLevel: null,
};

interface SearchResultsMapProps {
    hoveredEntity: null | HoveredMapEntity;
    isLoading: boolean;
    results: MapSearchResults | null;
    explicitBounds: BoundsInput | null;
    fallbackCenter: ILocation | null;
    geoSearchKey?: string | null;
    basemapPolicy: BasemapPolicy;
    onExplicitBoundsChanged: (northWest: ILocation, southEast: ILocation) => void;
    onMapViewChanged: (mapView: MapView) => void;
    renderMarkerControl?: (id: MapMarkerId) => JSX.Element;
    error?: unknown;
}

export const SearchResultsMap: React.FC<SearchResultsMapProps> = props => {
    const scaleUnit = useMapScaleUnit();
    const {
        hoveredEntity,
        explicitBounds,
        fallbackCenter,
        geoSearchKey,
        results,
        isLoading,
        basemapPolicy,
        onExplicitBoundsChanged,
        renderMarkerControl,
        onMapViewChanged,
        error,
    } = props;
    const mapStyleResult = useMapStyle(basemapPolicy);

    const wrapperDiv = React.useRef<HTMLDivElement>(null);
    const fixedMapDiv = React.useRef<HTMLDivElement>(null);
    const mapRef = React.useRef<MapRef>(null);
    const windowSize = useWindowSize();
    const [mapLoadedVersion, setMapLoadedVersion] = React.useState(0);
    const onMapLoad = React.useCallback(() => {
        setMapLoadedVersion(previous => previous + 1);
    }, []);

    const [state, localDispatch] = React.useReducer(reducer, initialState);

    const mapView: MapView = React.useMemo(() => {
        return {
            zoomLevel: state.zoomLevel,
            bounds: state.bounds,
            viewportHeight: windowSize.height * 0.7,
            viewportWidth: windowSize.width * 0.45,
        };
    }, [state.zoomLevel, state.bounds, windowSize]);

    const [debouncedMapView] = useDebouncedValue(mapView, 600);

    React.useEffect(() => {
        onMapViewChanged(debouncedMapView);
    }, [debouncedMapView]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (isLoading) {
            localDispatch({ type: "startLoading" });
        } else {
            localDispatch({ type: "endLoading" });
        }
    }, [isLoading]);
    React.useEffect(() => {
        if (!explicitBounds && state.isFreeMapView) {
            localDispatch({ type: "disableFreeMap" });
        }
    }, [explicitBounds, geoSearchKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const mapBounds: LngLatBounds | null = React.useMemo(() => {
        if (
            !results ||
            (results.entityClusters.length === 0 &&
                results.entityCoordinates.length === 0 &&
                !results.searchCoordinates)
        ) {
            return null;
        }
        const bounds = new LngLatBounds();
        results?.entityClusters.forEach(c => {
            bounds.extend([c.lng, c.lat]);
        });
        results?.entityCoordinates.forEach(c => {
            bounds.extend([c.lng, c.lat]);
        });
        if (results.searchCoordinates) {
            bounds.extend([
                parseFloat(results.searchCoordinates.lng),
                parseFloat(results.searchCoordinates.lat),
            ]);
        }
        return bounds;
    }, [results]);

    const shouldProcessEvents = useMapEvents(
        !state.isFreeMapView,
        wrapperDiv,
        fixedMapDiv,
        mapRef,
        mapBounds,
        mapLoadedVersion,
    );

    React.useEffect(() => {
        if (!state.popupId || !results) {
            return;
        }
        if (!results.entityCoordinates.some(c => c.id === state.popupId)) {
            localDispatch({ type: "clearPopup" });
        }
    }, [results, state.popupId]);

    const onBoundsUpdated = React.useCallback(
        (northWest: ILocation, southEast: ILocation) => {
            localDispatch({
                type: "endUserMapMove",
                zoomLevel: mapRef.current?.getMap().getZoom() ?? defaultZoomLevel,
                bounds: {
                    northWest: locationToCoordinates(northWest),
                    southEast: locationToCoordinates(southEast),
                },
            });

            onExplicitBoundsChanged(northWest, southEast);
        },
        [onExplicitBoundsChanged],
    );

    const onMoveEnd = React.useCallback(
        (e: unknown) => {
            if (shouldProcessEvents()) {
                if (
                    Object.prototype.hasOwnProperty.call(e, "automaticOperationType") &&
                    (e as { automaticOperationType: string }).automaticOperationType ===
                        "fitBounds"
                ) {
                    localDispatch({
                        type: "endAutoMapMove",
                    });
                } else {
                    const bounds = mapRef.current?.getMap().getBounds();
                    if (bounds) {
                        localDispatch({
                            type: "endUserMapMove",
                            zoomLevel:
                                mapRef.current?.getMap().getZoom() ?? defaultZoomLevel,
                            bounds: {
                                northWest: locationToCoordinates(
                                    latLngToILocation(bounds.getNorthWest()),
                                ),
                                southEast: locationToCoordinates(
                                    latLngToILocation(bounds.getSouthEast()),
                                ),
                            },
                        });
                    }
                }
            }
        },
        [shouldProcessEvents],
    );

    const doMapSearch = React.useCallback(() => {
        if (mapRef.current) {
            const bounds = mapRef.current.getMap().getBounds();
            if (bounds) {
                onBoundsUpdated(
                    latLngToILocation(bounds.getNorthWest()),
                    latLngToILocation(bounds.getSouthEast()),
                );
                localDispatch({ type: "startExplicitBoundsSearch" });
            }
        }
    }, [mapRef, onBoundsUpdated]);

    const flyTo = React.useCallback((lng: number, lat: number) => {
        if (mapRef.current) {
            mapRef.current.flyTo({
                center: [lng, lat],
                zoom: mapRef.current?.getMap().getZoom() + 2,
            });
        }
    }, []);

    const closePopup = React.useCallback(() => localDispatch({ type: "clearPopup" }), []);
    const setPopup = React.useCallback(
        (communityId: CommunityId) =>
            localDispatch({ type: "setPopup", popupId: communityId }),
        [],
    );

    if (mapStyleResult.status === "loading") {
        return (
            <div className="full-width">
                <Spinner />
            </div>
        );
    }

    if (mapStyleResult.status === "unavailable") {
        return (
            <InlineBanner type="warning">
                {mapStyleResult.reason === "featureDisabled"
                    ? "This map requires the Self-hosted Map Tiles feature flag to be enabled for this tenant."
                    : "Map tiles are temporarily unavailable. Try again shortly."}
            </InlineBanner>
        );
    }

    if (isLoading && !results) {
        return (
            <div className="full-width">
                <Spinner />
            </div>
        );
    }

    if (error) {
        const gqlError = error as ClientGraphqlError;
        if (
            typeof gqlError.hasErrorType === "function" &&
            gqlError.hasErrorType("INVALID_RECORD")
        ) {
            return (
                <InlineBanner type="warning">
                    The location data provided does not correspond to a real-world map
                    location. Please review your location search and try again.
                </InlineBanner>
            );
        }

        return (
            <Error componentName="NextgenFilteredCommunityList">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction */}
                {(error as any).message}
            </Error>
        );
    }

    if (!results) {
        return (
            <div className="full-width">
                <Spinner />
            </div>
        );
    }

    const mapStyle = mapStyleResult.style;

    const initialViewState = computeInitialViewState(mapBounds, fallbackCenter);

    const geoReference: ILocation | null = results.searchCoordinates
        ? {
              loc: [
                  parseFloat(results.searchCoordinates.lng),
                  parseFloat(results.searchCoordinates.lat),
              ],
          }
        : null;

    let actualMarkerHighlighted = false;
    const clusterMarkers: JSX.Element[] = [];
    results.entityCoordinates.forEach(c => {
        const isHovered = c.id === hoveredEntity?.entityId;
        if (isHovered) {
            actualMarkerHighlighted = true;
        }
        clusterMarkers.push(
            <SearchResultsMapMarker
                key={c.id}
                coordinate={c}
                isHovered={isHovered}
                onSelected={setPopup}
            />,
        );
    });

    const bounds = mapRef.current?.getMap().getBounds();
    const shouldHighlightCluster =
        !actualMarkerHighlighted &&
        bounds &&
        hoveredEntity &&
        hoveredEntity.location &&
        hoveredEntity.location.lat > bounds.getSouth() &&
        hoveredEntity.location.lat < bounds.getNorth() &&
        hoveredEntity.location.lng > bounds.getWest() &&
        hoveredEntity.location.lng < bounds.getEast() &&
        results.entityClusters.length > 0;
    const highlightedCluster: MapSearchCluster = shouldHighlightCluster
        ? results.entityClusters.reduce((acc, cluster) => {
              const distanceToCluster = getDistanceLatLng(
                  hoveredEntity.location,
                  cluster,
              );
              const distanceToClusterAcc = getDistanceLatLng(hoveredEntity.location, acc);
              return distanceToCluster < distanceToClusterAcc ? cluster : acc;
          })
        : null;
    results.entityClusters.forEach(c => {
        clusterMarkers.push(
            <SearchResultsMapCluster
                isHovered={highlightedCluster === c}
                flyTo={flyTo}
                key={`${c.lat}${c.lng}`}
                cluster={c}
            />,
        );
    });

    const entityPopupActual = results.entityCoordinates.find(c => c.id === state.popupId);

    return (
        <div className="community-map-anchor flex-1">
            <div className="top-0 sticky min-h-screen" ref={wrapperDiv}>
                <div
                    ref={fixedMapDiv}
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                    }}>
                    <MapErrorBoundary>
                        <Map
                            ref={mapRef}
                            onMoveEnd={onMoveEnd}
                            onLoad={onMapLoad}
                            mapLib={mapLib}
                            attributionControl={false}
                            initialViewState={initialViewState}
                            style={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                            }}
                            mapStyle={mapStyle}>
                            <MapLoadingIndicator isLoading={state.isLoading} />
                            <GeolocateControl position="top-left" />
                            <FullscreenControl position="top-left" />
                            <NavigationControl position="top-left" />
                            {isValidLocation(geoReference) ? (
                                <GeoCenter location={geoReference} />
                            ) : null}
                            {clusterMarkers}
                            {entityPopupActual ? (
                                <MarkerPopup
                                    onClose={closePopup}
                                    coordinate={entityPopupActual}
                                    childControlGenerator={renderMarkerControl}
                                />
                            ) : null}
                            <ScaleControl unit={scaleUnit} />
                        </Map>
                    </MapErrorBoundary>
                    <div className="top-0 right-0 absolute m-2">
                        {state.showUpdateSearch ? (
                            <SecondaryButton onClick={doMapSearch}>
                                Search this area
                            </SecondaryButton>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

const markerPinHeightPx = 40;

const markerPopupOffset: { [_ in PositionAnchor]: [number, number] } = {
    center: [0, 0],
    top: [0, 0],
    "top-left": [0, 0],
    "top-right": [0, 0],
    bottom: [0, -markerPinHeightPx],
    "bottom-left": [0, -markerPinHeightPx],
    "bottom-right": [0, -markerPinHeightPx],
    left: [0, 0],
    right: [0, 0],
};

const MarkerPopup: React.FC<{
    onClose: () => void;
    coordinate: MapSearchCoordinate;
    childControlGenerator?: (id: MapMarkerId, mapExperience?: boolean) => JSX.Element;
}> = props => {
    const { coordinate, childControlGenerator, onClose } = props;
    if (!location) {
        return null;
    }
    return (
        <Popup
            offset={markerPopupOffset}
            longitude={coordinate.lng}
            latitude={coordinate.lat}
            closeButton={false}
            maxWidth="none"
            closeOnClick={true}
            onClose={onClose}>
            <div>
                {coordinate.appLink ? (
                    <Link to={coordinate.appLink}>
                        <div className={`${defaultLinkStyle} text-lg`}>
                            {coordinate.name ?? NO_NAME}
                        </div>
                    </Link>
                ) : (
                    <div className={`text-lg`}>{coordinate.name ?? NO_NAME}</div>
                )}
                {childControlGenerator ? (
                    <div>{childControlGenerator(coordinate.id)}</div>
                ) : null}
            </div>
        </Popup>
    );
};
